import React from "react";
import { Plus, Trash2, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import CompanySection from "./form/CompanySection";
import ClientSection from "./form/ClientSection";
import NotesSection from "./form/NotesSection";

function DocumentForm({ document, onChange, onImproveText }) {
  function updateItem(id, field, value) {
    onChange({
      items: document.items.map(function(item) {
        return item.id === id ? { ...item, [field]: value } : item;
      })
    });
  }

  function addItem() {
    var maxId = 0;
    document.items.forEach(function(item) { if (item.id > maxId) maxId = item.id; });
    onChange({ 
      items: [...document.items, { 
        id: maxId + 1, 
        description: "", 
        details: "",
        quantity: 1, 
        unitPrice: 0,
        discount: 0
      }] 
    });
  }

  function removeItem(id) {
    if (document.items.length > 1) {
      onChange({ items: document.items.filter(function(item) { return item.id !== id; }) });
    }
  }

  function toggleIrpf(checked) {
    onChange({ irpfRate: checked ? 15 : 0 });
  }

  return (
    <div className="space-y-6" data-testid="document-form">
      {/* Document Header */}
      <section className="form-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="form-section-title mb-0">{document.type === "invoice" ? "Factura" : "Presupuesto"}</h2>
          <div className="px-4 py-2 bg-slate-100 rounded-md border border-slate-200">
            <span className="text-sm text-slate-700 font-bold">{document.number}</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-slate-600 text-sm">Fecha Emisión</Label>
            <Input type="date" value={document.date} onChange={function(e) { onChange({ date: e.target.value }); }} className="input-elite mt-1" data-testid="doc-date" />
          </div>
          <div>
            <Label className="text-slate-600 text-sm">Fecha Vencimiento</Label>
            <Input type="date" value={document.dueDate || ""} onChange={function(e) { onChange({ dueDate: e.target.value }); }} className="input-elite mt-1" data-testid="doc-due-date" />
          </div>
        </div>
      </section>

      <CompanySection company={document.company} onChange={onChange} />
      <ClientSection client={document.client} onChange={onChange} />

      {/* Items Section */}
      <section className="form-section">
        <div className="flex justify-between items-center mb-6">
          <h2 className="form-section-title mb-0">Conceptos y Desglose</h2>
          <Button onClick={addItem} size="sm" className="btn-primary" data-testid="add-item">
            <Plus size={16} className="mr-1" /> Añadir Concepto
          </Button>
        </div>

        <div className="space-y-6">
          {document.items.map(function(item, i) {
            var lineTotal = (item.quantity * item.unitPrice) - (item.discount || 0);
            return (
              <div key={item.id} className="p-5 bg-gradient-to-r from-slate-50 to-white rounded-md border border-slate-200 shadow-sm" data-testid={"item-" + i}>
                <div className="flex justify-between items-start mb-4">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Concepto {i + 1}</span>
                  <Button variant="ghost" size="sm" onClick={function() { removeItem(item.id); }} disabled={document.items.length <= 1} className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0" data-testid={"rm-item-" + i}>
                    <Trash2 size={16} />
                  </Button>
                </div>

                <div className="mb-4">
                  <Label className="text-slate-700 text-sm font-medium">Descripción del Servicio/Producto</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={item.description} onChange={function(e) { updateItem(item.id, "description", e.target.value); }} placeholder="Ej: Venta de vehículo BMW Serie 3 320d" className="input-elite" data-testid={"item-desc-" + i} />
                    <Button type="button" variant="ghost" size="sm" className="h-10 w-10 p-0 hover:bg-purple-100 shrink-0" onClick={function() { if(item.description) onImproveText(item.description, "item-" + item.id); }} disabled={!item.description} data-testid={"ai-item-" + i}>
                      <Sparkles size={16} className="text-purple-500" />
                    </Button>
                  </div>
                </div>

                <div className="mb-4">
                  <Label className="text-slate-700 text-sm font-medium">Desglose / Detalles</Label>
                  <div className="flex gap-2 mt-1">
                    <Textarea value={item.details || ""} onChange={function(e) { updateItem(item.id, "details", e.target.value); }} placeholder="Desglose detallado:&#10;• Características técnicas&#10;• Extras incluidos&#10;• Garantía y condiciones" className="input-elite min-h-[100px] text-sm" data-testid={"item-details-" + i} />
                    <Button type="button" variant="ghost" size="sm" className="h-10 w-10 p-0 hover:bg-purple-100 shrink-0 self-start" onClick={function() { if(item.details) onImproveText(item.details, "item-details-" + item.id); }} disabled={!item.details} data-testid={"ai-details-" + i}>
                      <Sparkles size={16} className="text-purple-500" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4 p-4 bg-slate-100 rounded-md">
                  <div>
                    <Label className="text-slate-600 text-xs">Cantidad</Label>
                    <Input type="number" min="1" value={item.quantity} onChange={function(e) { updateItem(item.id, "quantity", parseInt(e.target.value) || 1); }} className="input-elite mt-1" data-testid={"item-qty-" + i} />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-xs">Precio Unitario (€)</Label>
                    <Input type="number" min="0" step="0.01" value={item.unitPrice} onChange={function(e) { updateItem(item.id, "unitPrice", parseFloat(e.target.value) || 0); }} className="input-elite mt-1" data-testid={"item-price-" + i} />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-xs">Descuento (€)</Label>
                    <Input type="number" min="0" step="0.01" value={item.discount || 0} onChange={function(e) { updateItem(item.id, "discount", parseFloat(e.target.value) || 0); }} className="input-elite mt-1" data-testid={"item-discount-" + i} />
                  </div>
                  <div>
                    <Label className="text-slate-600 text-xs">Total Línea</Label>
                    <div className="input-elite mt-1 flex items-center justify-end pr-3 bg-slate-200 border-slate-300 font-semibold text-slate-700">
                      {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(lineTotal)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tax Settings */}
        <div className="mt-6 pt-4 border-t space-y-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Label className="text-slate-700 font-medium">IVA</Label>
              <Input type="number" min="0" max="100" value={document.taxRate} onChange={function(e) { onChange({ taxRate: parseFloat(e.target.value) || 0 }); }} className="input-elite w-20 text-center" data-testid="tax-rate" />
              <span className="text-slate-600">%</span>
            </div>
          </div>
          
          <div className="flex items-center gap-4 p-4 bg-orange-50 rounded-md border border-orange-200">
            <Checkbox 
              id="irpf-check" 
              checked={document.irpfRate > 0} 
              onCheckedChange={toggleIrpf}
              data-testid="irpf-checkbox"
            />
            <div className="flex-1">
              <Label htmlFor="irpf-check" className="text-slate-700 font-medium cursor-pointer">Aplicar retención IRPF</Label>
              <p className="text-xs text-slate-500">Marcar si el cliente es empresa y debe aplicarse retención</p>
            </div>
            {document.irpfRate > 0 && (
              <div className="flex items-center gap-2">
                <Input type="number" min="0" max="100" value={document.irpfRate} onChange={function(e) { onChange({ irpfRate: parseFloat(e.target.value) || 0 }); }} className="input-elite w-20 text-center" data-testid="irpf-rate" />
                <span className="text-slate-600">%</span>
              </div>
            )}
          </div>
        </div>
      </section>

      <NotesSection notes={document.notes} paymentTerms={document.paymentTerms} onChange={onChange} onImprove={onImproveText} />
    </div>
  );
}

export default DocumentForm;
